import { useRouter } from "next/router";
import type { FunctionComponent } from "react";
import type { PreloadedQuery } from "react-relay";
import { graphql, useFragment, usePreloadedQuery } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { useSearchContext } from "scmp-app/components/search/contexts";
import { query } from "scmp-app/components/search/search-explore-topics";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { SearchEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { searchExploreTopicsListQueue$key } from "scmp-app/queries/__generated__/searchExploreTopicsListQueue.graphql";
import type { searchExploreTopicsQuery } from "scmp-app/queries/__generated__/searchExploreTopicsQuery.graphql";

import { List, StyledSearchButton, StyledTopic } from "./styles";

type Props = {
  reference: PreloadedQuery<searchExploreTopicsQuery>;
};

export const SearchExploreTopicsList: FunctionComponent<Props> = ({ reference }) => {
  const queryReference = usePreloadedQuery<searchExploreTopicsQuery>(query, reference);
  const { searchRouteFunction } = useSearchContext();
  const router = useRouter();

  const fragment = useFragment<searchExploreTopicsListQueue$key>(
    graphql`
      fragment searchExploreTopicsListQueue on Queue {
        items(first: $first) {
          edges {
            node {
              __typename
              ... on Topic {
                entityId
                name
              }
            }
          }
        }
      }
    `,
    queryReference.queue,
  );

  const handleTopicClickTracking = (entityId: string) => {
    // GA4 21.4.1
    sendGA4Tracking({
      action: "click",
      category: "search",
      customized_parameters: {
        topic_id: entityId,
      },
      subcategory: "suggest_topics",
    } as SearchEvent);
  };

  const handleTopicClick = (entityId: string, name: string) => {
    handleTopicClickTracking(entityId);
    const searchPageRoute = searchRouteFunction?.({ q: name });
    if (!searchPageRoute) return;

    const { asPath, ...restSearchPageRoute } = searchPageRoute;
    void router.push(restSearchPageRoute, asPath);
  };

  return (
    <List>
      {fragment?.items?.edges.filter(notEmpty).map(({ node }) => {
        switch (node.__typename) {
          case "Topic":
            return (
              <StyledTopic key={node.entityId}>
                <StyledSearchButton
                  onClick={() => handleTopicClick(node.entityId, node.name)}
                  type="button"
                >
                  {node.name}
                </StyledSearchButton>
              </StyledTopic>
            );
          default:
            return null;
        }
      })}
    </List>
  );
};

SearchExploreTopicsList.displayName = "SearchExploreTopicsList";
